/**
 * Created: 2026-04-07
 * Initial commit: Add event dialog for slot page event cell click (feat/event/mr-9)
 *
 * Dialog component for displaying event details and signed-up players
 * when an event-reserved merged cell is clicked in the SlotsPage.
 */

import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { message } from '@homemaster/jslib'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import UserAvatar from '../components/UserAvatar'
import {
  CancelEventDocument,
  EventCategory,
  EventStatus,
  GetEventForSlotDocument,
} from '../gql/graphql'
import { formatEventTimeRange } from '../lib/dateUtils'
import EventFormDialog from './EventFormDialog'

gql`
  query GetEventForSlot($id: ID!) {
    event(id: $id) {
      id
      name
      location
      description
      startTime
      duration
      drawSize
      numSignups
      fee
      eventType
      status
      minRating
      maxRating
      signups {
        id
        signupTime
        user {
          id
          name
          avatar
          mobile
          gender
          ratingSingles
          openid
        }
      }
    }
  }
`

gql`
  mutation CancelEvent($eventId: UUID!) {
    cancelEvent(eventId: $eventId) {
      ... on GraphqlSuccess {
        success
      }
      ... on GraphqlRequestError {
        message
        code
      }
    }
  }
`

const EVENT_TYPE_CHIP: Record<
  EventCategory,
  { label: string; color: 'primary' | 'secondary' }
> = {
  [EventCategory.Freeplay]: { label: '畅打', color: 'secondary' },
  [EventCategory.Tournament]: { label: '比赛', color: 'primary' },
}

const EVENT_STATUS_LABEL: Record<EventStatus, string> = {
  [EventStatus.Created]: '报名中',
  [EventStatus.Started]: '进行中',
  [EventStatus.Completed]: '已完成',
  [EventStatus.Cancelled]: '已取消',
  [EventStatus.Aborted]: '已中止',
}

const EVENT_STATUS_COLOR: Record<
  EventStatus,
  'default' | 'primary' | 'success' | 'error' | 'warning'
> = {
  [EventStatus.Created]: 'primary',
  [EventStatus.Started]: 'warning',
  [EventStatus.Completed]: 'success',
  [EventStatus.Cancelled]: 'error',
  [EventStatus.Aborted]: 'error',
}

interface EventDialogProps {
  eventId: string
  onClose: () => void
}

export default function EventDialog({ eventId, onClose }: EventDialogProps) {
  const { data, loading } = useQuery(GetEventForSlotDocument, {
    variables: { id: eventId },
    fetchPolicy: 'network-only',
  })

  const [cancelEvent] = useMutation(CancelEventDocument)
  const [cancelling, setCancelling] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)

  const event = data?.event

  const isOver =
    !!event &&
    event.status !== EventStatus.Cancelled &&
    event.status !== EventStatus.Aborted &&
    event.status !== EventStatus.Completed &&
    new Date(event.startTime).getTime() + event.duration * 3600_000 < Date.now()

  const canCancel =
    event &&
    !isOver &&
    event.status !== EventStatus.Cancelled &&
    event.status !== EventStatus.Aborted &&
    event.status !== EventStatus.Completed &&
    (event.eventType !== EventCategory.Tournament ||
      event.status === EventStatus.Created)

  const canEdit = event && !isOver && event.status === EventStatus.Created

  const handleCancel = () => {
    message.prompt(
      '取消活动后，所有已报名的用户将自动获得全额退款并收到短信通知。此操作不可撤销，请确认后再继续。',
      {
        title: '确认取消活动',
        noLabel: '返回',
        yesLabel: '确认取消',
        onYesButton: async () => {
          setCancelling(true)
          try {
            const result = await cancelEvent({
              variables: { eventId: eventId as string },
            })
            const data = result.data?.cancelEvent
            if (data?.__typename === 'GraphqlRequestError') {
              message.error(data.message ?? '取消失败')
            } else {
              message.success('活动已成功取消')
              onClose()
            }
          } finally {
            setCancelling(false)
          }
        },
      },
    )
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 12 }}>
        {event && (
          <Chip
            label={EVENT_TYPE_CHIP[event.eventType].label}
            color={EVENT_TYPE_CHIP[event.eventType].color}
            size="small"
            sx={{ mr: 0.5, mb: 0.5 }}
          />
        )}
        {event?.name ?? '活动详情'}
        {event && (
          <Chip
            label={isOver ? '已结束' : EVENT_STATUS_LABEL[event.status]}
            size="small"
            color={isOver ? 'default' : EVENT_STATUS_COLOR[event.status]}
            sx={{ position: 'absolute', right: 44, top: 12 }}
          />
        )}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {event && (
          <>
            <Box
              sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}
            >
              <Box sx={{ gridColumn: 'span 2' }}>
                <Typography variant="caption" color="text.secondary">
                  时间
                </Typography>
                <Typography variant="body2">
                  {formatEventTimeRange(event.startTime, event.duration)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  地点
                </Typography>
                <Typography variant="body2">{event.location}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  报名费
                </Typography>
                <Typography variant="body2">
                  {Number(event.fee) === 0 ? '免费' : `¥${event.fee}`}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  报名人数
                </Typography>
                <Typography variant="body2">
                  {event.numSignups} / {event.drawSize}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  等级范围
                </Typography>
                <Typography variant="body2">
                  {event.minRating.toFixed(1)}–{event.maxRating.toFixed(1)}
                </Typography>
              </Box>
            </Box>

            {event.description && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  活动描述
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {event.description}
                </Typography>
              </Box>
            )}

            <Divider sx={{ mb: 1 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              已报名选手 ({event.signups.length})
            </Typography>

            {event.signups.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                暂无选手报名
              </Typography>
            ) : (
              <List dense disablePadding>
                {event.signups.map((signup, idx) => (
                  <ListItem key={signup.id} disableGutters>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 24, textAlign: 'right', mr: 1 }}
                    >
                      {idx + 1}.
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <UserAvatar user={signup.user} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {signup.user.ratingSingles != null && (
                          <Typography variant="caption" color="text.secondary">
                            {signup.user.ratingSingles.toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
      {(canEdit || canCancel) && (
        <DialogActions>
          {canEdit && (
            <Button variant="outlined" onClick={() => setEditDialogOpen(true)}>
              编辑
            </Button>
          )}
          {canCancel && (
            <Button
              variant="contained"
              color="error"
              onClick={handleCancel}
              disabled={cancelling}
            >
              取消活动
            </Button>
          )}
        </DialogActions>
      )}
      {editDialogOpen && event && (
        <EventFormDialog
          slots={[]}
          editEvent={event}
          onClose={() => setEditDialogOpen(false)}
          onSuccess={() => {
            setEditDialogOpen(false)
            // Apollo automatically updates the cache with the returned Event
          }}
        />
      )}
    </Dialog>
  )
}
